import React from 'react';
import { Box, Typography, Paper, Chip, Grid, Card, CardContent, Button } from '@mui/material';
import {
  Email as EmailIcon,
  AccountBalance as QuickBooksIcon,
  Payment as PaymentIcon,
  Description as TaxIcon,
  Hub as ZapierIcon,
  Cloud as CloudIcon,
  CheckCircle as CheckIcon,
  Schedule as ComingSoonIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

/**
 * Integrations Settings - Firmflow integration hub
 *
 * Integrations we can bring / integrate:
 */

const INTEGRATION_CATEGORIES = [
  {
    title: 'Mail & Calendar',
    description: 'Sync emails, folders, and calendars for client communication',
    integrations: [
      { id: 'gmail', name: 'Gmail', description: 'Sync emails, tags, folders, and inboxes', status: 'active', path: '/settings/email-calendar', icon: <EmailIcon /> },
      { id: 'outlook', name: 'Microsoft Outlook', description: 'Connect Outlook for email and calendar sync', status: 'active', path: '/settings/email-calendar', icon: <EmailIcon /> },
    ],
  },
  {
    title: 'Accounting Software',
    description: 'Sync clients, invoices, payments, and credits',
    integrations: [
      { id: 'quickbooks', name: 'QuickBooks Online', description: 'Sync clients, service items, invoices, payments, and credits', status: 'active', path: '/settings/quickbooks', icon: <QuickBooksIcon /> },
      { id: 'xero', name: 'Xero', description: 'Connect Xero for accounting data sync', status: 'coming_soon', icon: <QuickBooksIcon /> },
    ],
  },
  {
    title: 'Payments',
    description: 'Accept payments and manage billing',
    integrations: [
      { id: 'stripe', name: 'Stripe', description: 'Accept card payments, ACH, and manage invoices', status: 'active', path: '/settings/billing-settings', icon: <PaymentIcon /> },
      { id: 'helcim', name: 'Helcim', description: 'Alternative payment processing', status: 'coming_soon', icon: <PaymentIcon /> },
    ],
  },
  {
    title: 'Tax Prep Software',
    description: 'Print files from tax software directly into Firmflow',
    integrations: [
      { id: 'ultratax', name: 'UltraTax', description: 'Thomson Reuters UltraTax integration', status: 'coming_soon', icon: <TaxIcon /> },
      { id: 'lacerte', name: 'Lacerte', description: 'Intuit Lacerte tax software', status: 'coming_soon', icon: <TaxIcon /> },
      { id: 'proconnect', name: 'Intuit ProConnect', description: 'ProConnect Tax Online', status: 'coming_soon', icon: <TaxIcon /> },
      { id: 'drake', name: 'Drake Tax', description: 'Drake Tax software integration', status: 'coming_soon', icon: <TaxIcon /> },
      { id: 'cch', name: 'CCH Axcess Tax', description: 'Wolters Kluwer CCH Axcess', status: 'coming_soon', icon: <TaxIcon /> },
    ],
  },
  {
    title: 'Workflow Automation',
    description: 'Automate tasks between apps',
    integrations: [
      { id: 'zapier', name: 'Zapier', description: 'Connect to 5,000+ apps for workflow automation', status: 'coming_soon', icon: <ZapierIcon /> },
    ],
  },
  {
    title: 'Cloud Storage & Documents',
    description: 'File management and document sharing',
    integrations: [
      { id: 'googledrive', name: 'Google Drive', description: 'Sync and share documents', status: 'coming_soon', icon: <CloudIcon /> },
      { id: 'dropbox', name: 'Dropbox', description: 'Cloud file storage integration', status: 'coming_soon', icon: <CloudIcon /> },
      { id: 'onedrive', name: 'Microsoft OneDrive', description: 'OneDrive and SharePoint integration', status: 'coming_soon', icon: <CloudIcon /> },
    ],
  },
  {
    title: 'IRS & Government',
    description: 'Tax transcript and compliance tools',
    integrations: [
      { id: 'irs', name: 'IRS Transcripts', description: 'Request and review client transcripts', status: 'coming_soon', icon: <TaxIcon /> },
    ],
  },
];

const StatusChip = ({ status }) => {
  if (status === 'active') {
    return <Chip icon={<CheckIcon sx={{ fontSize: 16 }} />} label="Connected" size="small" color="success" variant="outlined" />;
  }
  return <Chip icon={<ComingSoonIcon sx={{ fontSize: 16 }} />} label="Coming Soon" size="small" variant="outlined" sx={{ color: '#666' }} />;
};

const IntegrationsSettings = () => {
  const navigate = useNavigate();

  const handleIntegrationClick = (integration) => {
    if (integration.status === 'active' && integration.path) {
      if (integration.path.startsWith('http') || integration.path.startsWith('/api')) {
        window.location.href = integration.path;
      } else {
        navigate(integration.path);
      }
    }
  };

  return (
    <Box sx={{ width: '100%', maxWidth: 900, py: 3, px: { xs: 2, sm: 3 } }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 600, color: '#111827', mb: 0.5 }}>
          Integrations
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Connect to other apps to automate tasks, sync data, and streamline your firm&apos;s workflow.
        </Typography>
      </Box>

      {INTEGRATION_CATEGORIES.map((category) => (
        <Paper key={category.title} sx={{ mb: 3, borderRadius: 2, boxShadow: 1, overflow: 'hidden' }}>
          <Box sx={{ px: 3, py: 2, background: '#f8f9fa', borderBottom: '1px solid #eee' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#333' }}>
              {category.title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {category.description}
            </Typography>
          </Box>
          <Box sx={{ p: 2 }}>
            <Grid container spacing={2}>
              {category.integrations.map((integration) => (
                <Grid item xs={12} sm={6} key={integration.id}>
                  <Card
                    variant="outlined"
                    sx={{
                      height: '100%',
                      cursor: integration.status === 'active' ? 'pointer' : 'default',
                      '&:hover': integration.status === 'active' ? { borderColor: '#1a38ff', bgcolor: '#fafbff' } : {},
                      transition: 'all 0.2s',
                    }}
                    onClick={() => handleIntegrationClick(integration)}
                  >
                    <CardContent sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                      <Box sx={{ color: integration.status === 'active' ? '#1a38ff' : '#999', mt: 0.5 }}>
                        {integration.icon}
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.5 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            {integration.name}
                          </Typography>
                          <StatusChip status={integration.status} />
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          {integration.description}
                        </Typography>
                        {integration.status === 'active' && integration.path && (
                          <Button
                            size="small"
                            startIcon={<SettingsIcon />}
                            sx={{ mt: 1, textTransform: 'none', px: 0 }}
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(integration.path);
                            }}
                          >
                            Manage
                          </Button>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Paper>
      ))}

      <Paper sx={{ p: 3, borderRadius: 2, bgcolor: '#f0f8ff', border: '1px solid #cce5ff' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#004085', mb: 1 }}>
          Want more integrations?
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Firmflow supports a public API for syncing client data. Contact us to discuss custom integrations or API access.
        </Typography>
      </Paper>
    </Box>
  );
};

export default IntegrationsSettings;
